import type { Monaco } from "@monaco-editor/react";
import {
  registerApexWithInstance,
  APEX_DARK_THEME_NAME,
  APEX_LIGHT_THEME_NAME,
} from "@ncoder/code-editor";

export interface CustomRendererProps {
  code: string;
  isIncomplete: boolean;
  language: string;
  meta?: string;
}

export interface ApexRendererCtx {
  isStreaming: boolean;
  showCopyButton: boolean;
}

export interface ParsedFenceMeta {
  artifactId?: string;
  artifactName?: string;
  metadataId?: string;
  metadataType?: string;
  operation?: string;
}

export const MONACO_LANG_ALIAS: Record<string, string> = {
  apex: "java",
  apexclass: "java",
  apextrigger: "java",
  apextest: "java",
  test: "java",
  java: "java",
  soql: "sql",
  sql: "sql",
  visualforce: "html",
  html: "html",
  javascript: "javascript",
  typescript: "typescript",
  jsx: "javascript",
  tsx: "typescript",
  react: "typescript",
  css: "css",
  aura: "javascript",
  lwc: "javascript",
  python: "python",
  go: "go",
  rust: "rust",
  ruby: "ruby",
  php: "php",
  kotlin: "kotlin",
  swift: "swift",
  c: "c",
  cpp: "cpp",
  csharp: "csharp",
  sh: "bash",
  shell: "bash",
  zsh: "bash",
  bash: "bash",
  console: "bash",
  text: "plaintext",
  plain: "plaintext",
  plaintext: "plaintext",
};

export const MONACO_LINE_HEIGHT = 19;
export const MONACO_PADDING = 16;
export const MONACO_MAX_HEIGHT = 600;

export const MONACO_OPTIONS = {
  readOnly: true,
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  fontSize: 13,
  lineNumbers: "on" as const,
  folding: false,
  glyphMargin: false,
  renderLineHighlight: "none" as const,
  overviewRulerLanes: 0,
  hideCursorInOverviewRuler: true,
  overviewRulerBorder: false,
  scrollbar: {
    verticalScrollbarSize: 8,
    horizontalScrollbarSize: 8,
    alwaysConsumeMouseWheel: false,
  },
  wordWrap: "off" as const,
  automaticLayout: true,
  contextmenu: false,
  guides: { indentation: false },
  padding: { top: 8, bottom: 8 },
};

export const CHAT_APEX_THEME = {
  dark: APEX_DARK_THEME_NAME,
  light: APEX_LIGHT_THEME_NAME,
};

export function beforeChatMonacoMount(monaco: Monaco) {
  registerApexWithInstance(monaco);
}

export function parseFenceMeta(meta?: string): ParsedFenceMeta {
  if (!meta) return {};
  const out: ParsedFenceMeta = {};
  const matches = meta.matchAll(/(\w+)=("[^"]*"|\S+)/g);
  for (const [, rawKey, rawValue] of matches) {
    const key = rawKey.trim();
    const value = decodeURIComponent(rawValue.replace(/^"|"$/g, "").trim());
    if (!value) continue;
    if (key === "artifact_id") out.artifactId = value;
    if (key === "artifact_name") out.artifactName = value;
    if (key === "metadata_id") out.metadataId = value;
    if (key === "metadata_type") out.metadataType = value;
    if (key === "operation") out.operation = value;
  }
  return out;
}
